/**
 * A ficha de qualquer coleção do mundo.
 *
 * <p>Uma tela para as dez: os campos, seus tipos e sua ordem vêm do esquema. Adicionar
 * um campo lá o faz aparecer aqui sem tocar neste arquivo.
 *
 * <p><b>Como salva:</b> campo de texto grava ao sair dele; vínculo, tag e seleção
 * múltipla gravam na hora. Não há botão "salvar" porque isto é um documento vivo,
 * escrito a seis mãos — e um botão esquecido é trabalho perdido.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { CampoDeAreaComComentarios } from './ComentariosDoTexto';
import { divisaDaFicha } from './divisaDaFicha';
import { CATEGORIAS_DE_ITEM, COR_DA_RARIDADE, definicaoDe } from './esquema';
import type { Campo } from './esquema';
import { ICONES_DE_REGIAO, IconeDeRegiao } from './IconeDeRegiao';
import { CapaAutenticada, useImagemAutenticada } from '../../comum/ImagemAutenticada';
import { COLETAS, COMBATE, CRIACOES, corDe } from '../gamedesign/pericias';
import * as mundoApi from './mundoApi';
import { PericiaIcone } from './PericiaIcone';
import { SeletorDeRelacao } from './SeletorDeRelacao';
import type { Colecao, Ficha, Ingrediente, Pericia, Resumo, Vinculos } from './tipos';
import './mundo.css';

/** As perícias do Game Design no seletor de NPC, quest e evento, na ordem da Ficha. */
const GRUPOS_DE_PERICIA = [
  { rotulo: 'Coleta', pericias: COLETAS },
  { rotulo: 'Criação', pericias: CRIACOES },
  { rotulo: 'Combate', pericias: COMBATE },
];

export function MundoFicha() {
  const { colecao, id } = useParams<{ colecao: Colecao; id: string }>();
  const colecaoId = (colecao ?? 'regioes') as Colecao;
  const fichaId = Number(id);
  const definicao = definicaoDe(colecaoId);
  const navegar = useNavigate();
  const fila = useQueryClient();

  const [aviso, setAviso] = useState<{ texto: string; ok: boolean } | null>(null);
  const [confirmando, setConfirmando] = useState<Vinculos | null>(null);

  const { data: ficha, isLoading, error } = useQuery<Ficha>({
    queryKey: ['mundo', colecaoId, fichaId],
    queryFn: () => mundoApi.carregar(colecaoId, fichaId),
  });

  const { data: pericias } = useQuery<Pericia[]>({
    queryKey: ['mundo', 'pericias'],
    queryFn: () => mundoApi.carregarMapa().then((m) => m.pericias),
    staleTime: Infinity,
  });

  /*
   * Quem é conteúdo do jogador. Vem do backend porque é de lá que a exportação para a
   * engine vai sair — se a tela também guardasse a lista, as duas teriam que concordar,
   * e duas listas que precisam concordar divergem.
   *
   * `staleTime: Infinity` como as perícias: é metadado, não muda entre visitas.
   */
  const { data: inGame, isLoading: carregandoInGame } = useQuery({
    queryKey: ['mundo', 'in-game'],
    queryFn: mundoApi.carregarInGame,
    staleTime: Infinity,
  });

  const salvar = useMutation({
    mutationFn: (mudancas: Record<string, unknown>) =>
      mundoApi.salvar(colecaoId, fichaId, mudancas),
    onSuccess: (atualizada) => {
      fila.setQueryData(['mundo', colecaoId, fichaId], atualizada);
      // A listagem mostra nome e imagem: qualquer um dos dois pode ter mudado.
      void fila.invalidateQueries({ queryKey: ['mundo', colecaoId], exact: true });
      setAviso({ texto: 'Salvo', ok: true });
    },
    onError: (e) =>
      setAviso({ texto: e instanceof Error ? e.message : 'Falha ao salvar.', ok: false }),
  });

  const excluir = useMutation({
    mutationFn: (confirmado: boolean) => mundoApi.excluir(colecaoId, fichaId, confirmado),
    onSuccess: async () => {
      await fila.invalidateQueries({ queryKey: ['mundo', colecaoId] });
      navegar(`/mundo/${colecaoId}`);
    },
    onError: (e) =>
      setAviso({ texto: e instanceof Error ? e.message : 'Falha ao excluir.', ok: false }),
  });

  // O aviso "salvo" some sozinho; erro fica até a próxima ação.
  useEffect(() => {
    if (!aviso?.ok) return;
    const t = setTimeout(() => setAviso(null), 2000);
    return () => clearTimeout(t);
  }, [aviso]);

  // A divisa entra na espera junto da ficha: desenhar antes dela chegar mostraria a
  // ficha sem a caixa dourada por um quadro, e a moldura apareceria depois.
  if (isLoading || carregandoInGame) return <div className="mundo mu-nada">Carregando…</div>;
  if (error || !ficha) {
    return (
      <div className="mundo mu-nada erro">
        {error instanceof Error ? error.message : 'Ficha não encontrada.'}
      </div>
    );
  }

  const registro = ficha as unknown as Record<string, unknown>;

  /*
   * O que fica na caixa do topo, e o que fica logo abaixo dela. A régua mora em
   * `divisaDaFicha`, e o que é conteúdo do jogador vem do backend — ver lá.
   */
  const { temCaixa, dentroDaCaixa, foraDaCaixa } = divisaDaFicha(definicao, inGame);

  // Dentro da caixa, duas naturezas: rótulos curtos que ficam em linha ao lado do nome,
  // e a narrativa, que é texto para ser lido. Separá-los é o que permite à narrativa
  // crescer até a altura da imagem, em vez de ficar espremida entre os rótulos.
  const cabecalho = dentroDaCaixa.filter((c) => c.tipo !== 'imagem' && c.tipo !== 'area');
  const cabecalhoLongo = dentroDaCaixa.filter((c) => c.tipo === 'area');
  const corpo = definicao.campos.filter((c) => !c.cabecalho);
  const campoImagem = definicao.campos.find((c) => c.tipo === 'imagem');

  function aplicar(chave: string, valor: unknown) {
    salvar.mutate({ [chave]: valor });
  }

  /**
   * A ficha da carta é a única com desenho próprio: arte grande à esquerda, campos à
   * direita, e a moldura na cor da raridade.
   *
   * <p>A cor vem de `COR_DA_RARIDADE`, a mesma que pinta o filtro e a grade da
   * listagem — uma carta Épica é roxa nos três lugares. Sem raridade escolhida a
   * moldura fica na borda neutra de todo card, e não numa cor inventada: "ainda não
   * decidimos" não pode parecer uma sexta raridade.
   */
  const gamificada = definicao.layout === 'carta';
  const estiloDaMoldura = gamificada
    ? ({
        '--raridade':
          COR_DA_RARIDADE[(registro.raridade as string) ?? ''] ?? 'var(--card-brd)',
      } as React.CSSProperties)
    : undefined;

  /**
   * Sempre abre a confirmação do próprio GG Pilot, mesmo quando não há vínculo.
   *
   * <p>Antes, a ficha sem vínculos caía num {@code window.confirm} — a caixa cinza do
   * navegador, com a fonte dele e um "OK / Cancelar" que não diz o que vai acontecer.
   * Excluir é a ação mais séria da tela; ela não pode ser a única que sai do desenho
   * do produto.
   */
  async function pedirExclusao() {
    try {
      setConfirmando(await mundoApi.vinculos(colecaoId, fichaId));
    } catch (e) {
      setAviso({ texto: e instanceof Error ? e.message : 'Falha.', ok: false });
    }
  }

  return (
    <div className="mundo">
      <div className="fi-trilha">
        <button onClick={() => navegar(`/mundo/${colecaoId}`)}>{definicao.rotulo}</button>
        <span>›</span>
        <b>{ficha.nome}</b>
      </div>

      <div className={`fi-corpo${gamificada ? ' baralho' : ''}`} style={estiloDaMoldura}>
        {/*
          Na ficha gamificada a arte sai do cabeçalho e vira a coluna da esquerda —
          é o produto final da carta, e não a ilustração do que está escrito.
        */}
        {gamificada && campoImagem && (
          <div className="fi-arte">
            <CampoDeImagem
              valor={registro[campoImagem.chave] as string | undefined}
              aoMudar={(url) => aplicar(campoImagem.chave, url ?? '')}
              aoAvisar={setAviso}
            />
            <div className="fi-arte-selo">
              {(registro.raridade as string) || 'sem raridade'}
              {registro.numero ? <b>nº {registro.numero as string}</b> : null}
            </div>
          </div>
        )}

        <div className="fi-corpo-txt">
      {/*
        A caixa do topo, quando a coleção tem conteúdo in-game, é a divisa da ficha: o
        que está dentro dela o jogador vê; o que está fora é anotação do time. O rótulo
        existe porque a divisa não se lê sozinha — sem ele, a caixa é só um cabeçalho
        mais bonito, e a pergunta "isto aparece no jogo?" continua sem resposta.
      */}
      <div
        className={`fi-cabeca${cabecalhoLongo.length > 0 ? ' com-narrativa' : ''}${
          temCaixa ? ' in-game' : ''
        }`}
      >
        {temCaixa && (
          <div className="fi-in-game-selo">
            <span className="fi-in-game-rot">Conteúdo in-game</span>
            <span className="fi-in-game-nota">o que o jogador vê</span>
          </div>
        )}

        {!gamificada && campoImagem && (
          <CampoDeImagem
            valor={registro[campoImagem.chave] as string | undefined}
            aoMudar={(url) => aplicar(campoImagem.chave, url ?? '')}
            aoAvisar={setAviso}
          />
        )}

        <div className="fi-cabeca-txt">
          <input
            className="fi-nome"
            defaultValue={ficha.nome}
            key={ficha.nome}
            onBlur={(e) => {
              const novo = e.target.value.trim();
              if (novo && novo !== ficha.nome) aplicar('nome', novo);
            }}
          />

          {/* Cada campo diz o que é. Sem o rótulo, o cabeçalho da carta era uma fila de
              caixas — "Comum", "Drop", "2", "Não tem" — em que só quem já conhecia a
              planilha sabia o que era raridade, o que era tipo e o que era pontuação. */}
          <div className="fi-cabeca-campos">
            {cabecalho.map((campo) => (
              <div key={campo.chave} className="fi-cab-campo">
                <label>{campo.rotulo}</label>
                <CampoDaFicha
                  campo={campo}
                  registro={registro}
                  pericias={pericias ?? []}
                  aoAplicar={aplicar}
                  aoAbrir={(c, i) => navegar(`/mundo/${c}/${i}`)}
                  fichaId={fichaId}
                  colecao={colecaoId}
                  aoRecarregar={() =>
                    fila.invalidateQueries({ queryKey: ['mundo', colecaoId, fichaId] })
                  }
                />
              </div>
            ))}
          </div>

          {cabecalhoLongo.map((campo) => (
            <div key={campo.chave} className="fi-cab-longo">
              <label>{campo.rotulo}</label>
              <CampoDaFicha
                campo={campo}
                registro={registro}
                pericias={pericias ?? []}
                aoAplicar={aplicar}
                aoAbrir={(c, i) => navegar(`/mundo/${c}/${i}`)}
                fichaId={fichaId}
                colecao={colecaoId}
                aoRecarregar={() =>
                  fila.invalidateQueries({ queryKey: ['mundo', colecaoId, fichaId] })
                }
              />
            </div>
          ))}
        </div>

        <div className="fi-acoes">
          <button className="fi-excluir" onClick={() => void pedirExclusao()}>
            Excluir
          </button>
          {aviso && <span className={`fi-aviso${aviso.ok ? ' ok' : ''}`}>{aviso.texto}</span>}
        </div>
      </div>

      {/*
        Os campos do topo que NÃO são do jogador — a Região da Área, o "Pertence a" do
        Local, o Tipo do Evento. Ficam no topo por serem estado da ficha, e por isso do
        lado de fora da moldura: dentro dela, seriam lidos como conteúdo do jogo.
      */}
      {foraDaCaixa.length > 0 && (
        <div className="fi-fora-da-caixa">
          {foraDaCaixa.map((campo) => (
            <div key={campo.chave} className="fi-cab-campo">
              <label>{campo.rotulo}</label>
              <CampoDaFicha
                campo={campo}
                registro={registro}
                pericias={pericias ?? []}
                aoAplicar={aplicar}
                aoAbrir={(c, i) => navegar(`/mundo/${c}/${i}`)}
                fichaId={fichaId}
                colecao={colecaoId}
                aoRecarregar={() =>
                  fila.invalidateQueries({ queryKey: ['mundo', colecaoId, fichaId] })
                }
              />
            </div>
          ))}
        </div>
      )}

      <div className="fi-campos">
        {corpo.map((campo) => (
          <div
            key={campo.chave}
            className={`fi-campo${campo.larga ? ' larga' : ''}${campo.terco ? ' terco' : ''}${
              campo.quarto ? ' quarto' : ''
            }`}
          >
            <label>
              {campo.rotulo}
              {campo.tipo === 'derivado' && <span className="fi-derivado">Derivado</span>}
            </label>
            <CampoDaFicha
              campo={campo}
              registro={registro}
              pericias={pericias ?? []}
              aoAplicar={aplicar}
              aoAbrir={(c, i) => navegar(`/mundo/${c}/${i}`)}
              fichaId={fichaId}
              colecao={colecaoId}
              aoRecarregar={() =>
                fila.invalidateQueries({ queryKey: ['mundo', colecaoId, fichaId] })
              }
            />
          </div>
        ))}
      </div>

        </div>
      </div>

      <div className="fi-rodape">
        {ficha.autoria.criadoPor && <span>Criada por {ficha.autoria.criadoPor}</span>}
        {ficha.autoria.atualizadoPor && (
          <span>· Última alteração de {ficha.autoria.atualizadoPor}</span>
        )}
      </div>

      {confirmando && (
        <div className="fi-modal" onClick={() => setConfirmando(null)}>
          <div className="fi-modal-cx perigo" onClick={(e) => e.stopPropagation()}>
            <div className="fi-modal-selo">Excluir</div>
            <h3>{confirmando.nome}</h3>

            {confirmando.dependencias.length > 0 ? (
              <>
                <p>Estas fichas apontam para ela e perderão o vínculo:</p>
                <ul>
                  {confirmando.dependencias.map((d) => (
                    <li key={d.colecao + d.rotulo}>
                      <b>{d.quantidade}</b> {d.rotulo.toLowerCase()}
                    </li>
                  ))}
                </ul>
                <p className="fi-modal-nota">
                  Nenhuma delas será excluída — só o vínculo com esta ficha desaparece.
                </p>
              </>
            ) : (
              <p className="fi-modal-nota">
                Nada aponta para esta ficha. A exclusão não afeta mais nada — e não tem volta.
              </p>
            )}

            <div className="fi-modal-btns">
              <button onClick={() => setConfirmando(null)}>Cancelar</button>
              <button
                className="perigo"
                onClick={() => {
                  const tinhaVinculos = confirmando.dependencias.length > 0;
                  setConfirmando(null);
                  excluir.mutate(tinhaVinculos);
                }}
              >
                Excluir {definicao.singular}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ campos

interface CampoProps {
  campo: Campo;
  registro: Record<string, unknown>;
  pericias: Pericia[];
  aoAplicar: (chave: string, valor: unknown) => void;
  aoAbrir: (colecao: Colecao, id: number) => void;
  colecao: Colecao;
  fichaId: number;
  aoRecarregar: () => void;
}

function CampoDaFicha({
  campo,
  registro,
  pericias,
  aoAplicar,
  aoAbrir,
  colecao,
  fichaId,
  aoRecarregar,
}: CampoProps) {
  const valor = registro[campo.chave];

  switch (campo.tipo) {
    case 'texto':
      return (
        <input
          className="fi-txt"
          defaultValue={(valor as string) ?? ''}
          onBlur={(e) => {
            if (e.target.value !== ((valor as string) ?? '')) {
              aoAplicar(campo.chave, e.target.value);
            }
          }}
        />
      );

    case 'area':
      return (
        <CampoDeAreaComComentarios
          colecao={colecao}
          fichaId={fichaId}
          campoChave={campo.chave}
          valor={(valor as string) ?? ''}
          larga={campo.larga}
          aoAplicar={(novo) => aoAplicar(campo.chave, novo)}
        />
      );

    case 'cor':
      return (
        <input
          className="fi-cor"
          type="color"
          value={(valor as string) || '#c9a25a'}
          onChange={(e) => aoAplicar(campo.chave, e.target.value)}
        />
      );

    case 'subcategoriaDeItem': {
      // As opções vêm da categoria escolhida na própria ficha. Só equipamento,
      // coletável e craft se subdividem; nas outras o campo some em vez de ficar
      // vazio pedindo uma escolha que não existe.
      const categoria = CATEGORIAS_DE_ITEM.find((c) => c.id === registro.categoria);
      if (!categoria || categoria.sub.length === 0) return null;
      return (
        <select
          className="fi-sel"
          value={(valor as string) ?? ''}
          onChange={(e) => aoAplicar(campo.chave, e.target.value)}
        >
          <option value="">—</option>
          {categoria.sub.map((s) => (
            <option key={s.id} value={s.id}>
              {s.rotulo}
            </option>
          ))}
        </select>
      );
    }

    case 'receita':
      return (
        <Receita
          itemId={fichaId}
          ingredientes={(valor as Ingrediente[]) ?? []}
          aoAbrir={aoAbrir}
          aoRecarregar={aoRecarregar}
        />
      );

    case 'iconeDaRegiao': {
      // Desenhado já na cor da própria região: escolher o ícone e ver como ele vai
      // aparecer são a mesma ação.
      const cor = (registro.corMapa as string) || undefined;
      return (
        <div className="fi-icones">
          {ICONES_DE_REGIAO.map((op) => (
            <button
              key={op.id}
              type="button"
              className={`fi-icone${valor === op.id ? ' on' : ''}`}
              title={op.rotulo}
              onClick={() => aoAplicar(campo.chave, valor === op.id ? '' : op.id)}
            >
              <IconeDeRegiao icone={op.id} cor={cor} tamanho={26} />
              <span>{op.rotulo}</span>
            </button>
          ))}
        </div>
      );
    }

    /*
     * Um sim/não marcado à mão — hoje, "esta área está pronta".
     *
     * Vai como booleano, e não como texto: aqui não existe o "vazio limpa" dos outros
     * campos, porque desmarcar já é o valor falso. O clique grava na hora, como o resto
     * da ficha, e o rótulo fica ao lado para a caixa não ficar órfã no formulário.
     */
    case 'check':
      return (
        <label className="fi-check">
          <input
            type="checkbox"
            checked={valor === true}
            onChange={(e) => aoAplicar(campo.chave, e.target.checked)}
          />
          <span>{campo.dica ?? 'Sim'}</span>
        </label>
      );

    /**
     * Vários booleanos sob um rótulo só — hoje, a Loja do NPC.
     *
     * <p>Dois `check` separados dariam duas linhas rotuladas "Compra" e "Venda", e o
     * leitor teria de deduzir que as duas falam de loja. Aqui o rótulo diz o assunto
     * uma vez, e as caixas dizem o que varia dentro dele.
     */
    case 'checks':
      return (
        <div className="fi-checks">
          {(campo.subcampos ?? []).map((sub) => (
            <label key={sub.chave} className="fi-check">
              <input
                type="checkbox"
                checked={registro[sub.chave] === true}
                onChange={(e) => aoAplicar(sub.chave, e.target.checked)}
              />
              <span>{sub.rotulo}</span>
            </label>
          ))}
        </div>
      );

    case 'quadros':
      return (
        <Quadros
          quadros={(valor as QuadroDeTexto[]) ?? []}
          maximo={campo.maximo ?? 4}
          aoMudar={(novos) => aoAplicar(campo.chave, novos)}
        />
      );

    case 'selecao':
      return (
        <select
          className="fi-sel"
          value={(valor as string) ?? ''}
          onChange={(e) => aoAplicar(campo.chave, e.target.value)}
        >
          <option value="">—</option>
          {(campo.opcoes ?? []).map((o) => (
            <option key={o} value={o}>
              {campo.chave === 'pericia'
                ? pericias.find((p) => p.id === o)?.rotulo ?? o
                : o}
            </option>
          ))}
        </select>
      );

    case 'multi': {
      const marcados = new Set((valor as string[]) ?? []);
      return (
        <div className="fi-multi">
          {(campo.opcoes ?? []).map((o) => (
            <button
              key={o}
              type="button"
              className={`fi-opc${marcados.has(o) ? ' on' : ''}`}
              onClick={() => {
                const novo = new Set(marcados);
                if (novo.has(o)) novo.delete(o);
                else novo.add(o);
                aoAplicar(campo.chave, [...novo]);
              }}
            >
              {o}
            </button>
          ))}
        </div>
      );
    }

    case 'pericias': {
      const marcadas = new Set((valor as string[]) ?? []);
      return (
        <div className="fi-multi">
          {pericias.map((p) => (
            <button
              key={p.id}
              type="button"
              className={`fi-opc com-icone${marcadas.has(p.id) ? ' on' : ''}`}
              style={{ '--c': p.cor } as React.CSSProperties}
              onClick={() => {
                const novo = new Set(marcadas);
                if (novo.has(p.id)) novo.delete(p.id);
                else novo.add(p.id);
                aoAplicar(campo.chave, [...novo]);
              }}
            >
              {/* O mesmo desenho que aparece no hexágono do mapa: quem marca aqui já
                  vê como a área vai ficar lá. */}
              <PericiaIcone pericia={p.id} cor={marcadas.has(p.id) ? p.cor : undefined} />
              {p.rotulo}
            </button>
          ))}
        </div>
      );
    }

    case 'periciasDoDesign': {
      const marcadas = new Set((valor as string[]) ?? []);
      return (
        <div className="fi-pericias-do-design">
          {GRUPOS_DE_PERICIA.map((g) => (
            <div key={g.rotulo} className="fi-pericias-grupo">
              <span className="fi-pericias-rotulo">{g.rotulo}</span>
              <div className="fi-multi">
                {g.pericias.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    className={`fi-opc com-icone${marcadas.has(p.id) ? ' on' : ''}`}
                    style={{ '--c': corDe(p) } as React.CSSProperties}
                    // A que só entra depois da Season 1 continua visível e fechada,
                    // como na Ficha — a não ser que já esteja marcada, para poder sair.
                    disabled={p.depoisDaSeason1 && !marcadas.has(p.id)}
                    title={p.depoisDaSeason1 ? 'Só depois da Season 1' : undefined}
                    onClick={() => {
                      const novo = new Set(marcadas);
                      if (novo.has(p.id)) novo.delete(p.id);
                      else novo.add(p.id);
                      aoAplicar(campo.chave, [...novo]);
                    }}
                  >
                    <PericiaIcone pericia={p.id} cor={marcadas.has(p.id) ? corDe(p) : undefined} />
                    {p.rotulo}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      );
    }

    case 'tags':
      return (
        <input
          className="fi-txt"
          defaultValue={((valor as string[]) ?? []).join(', ')}
          placeholder="Separe por vírgula"
          onBlur={(e) =>
            aoAplicar(
              campo.chave,
              e.target.value.split(',').map((s) => s.trim()).filter(Boolean)
            )
          }
        />
      );

    case 'relacao':
      return (
        <SeletorDeRelacao
          alvo={campo.alvo!}
          selecionados={(valor as Resumo[]) ?? []}
          aoAbrir={aoAbrir}
          aoMudar={(ids) => aoAplicar(campo.chave, ids)}
        />
      );

    case 'relacao1': {
      const unico = valor as Resumo | undefined;
      return (
        <SeletorDeRelacao
          alvo={campo.alvo!}
          unico
          selecionados={unico ? [unico] : []}
          aoAbrir={aoAbrir}
          aoMudar={(ids) =>
            ids.length === 0
              ? aoAplicar(`remover${maiuscula(campo.chave)}`, true)
              : aoAplicar(`${campo.chave}Id`, ids[0])
          }
        />
      );
    }

    case 'pertenceA':
      return (
        <PertenceA
          area={registro.area as Resumo | undefined}
          pai={registro.pai as Resumo | undefined}
          padrao={registro.padrao === true}
          aoAplicar={aoAplicar}
          aoAbrir={aoAbrir}
        />
      );

    case 'derivado':
      return (
        <Derivado
          campo={campo}
          itens={(valor as Resumo[]) ?? []}
          aoAbrir={aoAbrir}
          colecao={colecao}
          fichaId={fichaId}
          localPadraoId={(registro.localPadrao as Resumo | undefined)?.id}
          aoRecarregar={aoRecarregar}
        />
      );

    case 'relacoesDeRaca':
      return <RelacoesDeRaca racaId={fichaId} registro={registro} aoAbrir={aoAbrir} aoRecarregar={aoRecarregar} />;

    default:
      return null;
  }
}

const maiuscula = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** Um quadro repetível: título e texto. Hoje, um diálogo do NPC. */
interface QuadroDeTexto {
  /** Vem do servidor e serve de chave de render — não volta no PATCH. */
  id?: number;
  titulo?: string;
  texto?: string;
}

/**
 * Quadros repetíveis, dois por linha, com o "+" ocupando a célula seguinte.
 *
 * <p>O "+" fica <b>dentro da grade</b>, e não abaixo dela, porque é ali que o próximo
 * quadro vai nascer — o botão ocupa antes o lugar que o conteúdo vai ocupar depois. Um
 * botão embaixo diria "adicionar" sem dizer onde.
 *
 * <p>Começa em zero: uma ficha nova mostra só o "+", e nenhum quadro vazio pedindo para
 * ser preenchido. No máximo, o "+" some — o limite se explica sozinho, sem mensagem de
 * erro, porque quem não vê o botão não tenta.
 *
 * <p>Cada edição manda a lista inteira, como todo vínculo do módulo. Salvar por quadro
 * exigiria um endpoint por quadro e uma identidade estável para cada um; a lista
 * inteira não exige nem uma coisa nem outra, e reordenar passa a ser de graça.
 */
function Quadros({
  quadros,
  maximo,
  aoMudar,
}: {
  quadros: QuadroDeTexto[];
  maximo: number;
  aoMudar(novos: { titulo: string; texto: string }[]): void;
}) {
  const enviar = (lista: QuadroDeTexto[]) =>
    aoMudar(lista.map((q) => ({ titulo: q.titulo ?? '', texto: q.texto ?? '' })));

  function trocar(indice: number, chave: 'titulo' | 'texto', texto: string) {
    enviar(quadros.map((q, i) => (i === indice ? { ...q, [chave]: texto } : q)));
  }

  return (
    <div className="fi-quadros">
      {quadros.map((quadro, indice) => (
        <div className="fi-quadro" key={quadro.id ?? indice}>
          <div className="fi-quadro-topo">
            <input
              className="fi-txt"
              placeholder="Título"
              defaultValue={quadro.titulo ?? ''}
              onBlur={(e) => {
                if (e.target.value !== (quadro.titulo ?? '')) trocar(indice, 'titulo', e.target.value);
              }}
            />
            <button
              type="button"
              className="fi-quadro-x"
              title="Remover este diálogo"
              onClick={() => enviar(quadros.filter((_, i) => i !== indice))}
            >
              ×
            </button>
          </div>
          <textarea
            className="fi-area"
            rows={4}
            placeholder="Texto"
            defaultValue={quadro.texto ?? ''}
            onBlur={(e) => {
              if (e.target.value !== (quadro.texto ?? '')) trocar(indice, 'texto', e.target.value);
            }}
          />
        </div>
      ))}

      {quadros.length < maximo && (
        <button
          type="button"
          className="fi-quadro-mais"
          onClick={() => enviar([...quadros, { titulo: '', texto: '' }])}
        >
          <span>+</span>
          {quadros.length === 0 ? 'Primeiro diálogo' : 'Mais um'}
        </button>
      )}
    </div>
  );
}

/**
 * Onde o local está: dentro de uma área, ou dentro de outro local.
 *
 * <p>É uma escolha só. Antes eram dois campos independentes — "Área" e "Dentro de" —,
 * e preencher os dois deixava a ficha dizendo duas coisas diferentes: a taverna na
 * Área 3 e, ao mesmo tempo, dentro de um porão da Área 5. Aqui se escolhe primeiro o
 * tipo do pai e depois quem ele é; a área de um local aninhado vem de quem o abriga.
 */
function PertenceA({
  area,
  pai,
  padrao,
  aoAplicar,
  aoAbrir,
}: {
  area?: Resumo;
  pai?: Resumo;
  /** O local que representa a própria área: o vínculo dele vem dela e não se edita. */
  padrao: boolean;
  aoAplicar: (chave: string, valor: unknown) => void;
  aoAbrir: (colecao: Colecao, id: number) => void;
}) {
  const [modo, setModo] = useState<'areas' | 'locais'>(pai ? 'locais' : 'areas');

  // Salvar troca o pai pelo servidor; o botão marcado acompanha o que voltou.
  useEffect(() => setModo(pai ? 'locais' : 'areas'), [pai?.id]);

  if (padrao) {
    return (
      <span className="fi-pertence-fixo">
        {area?.nome ?? 'Sem área'} — esta ficha é a própria área
      </span>
    );
  }

  const escolhido = modo === 'locais' ? pai : area;

  return (
    <div className="fi-pertence">
      <span className="fi-pertence-rot">Pertence a</span>

      <div className="fi-pertence-modos">
        <button
          type="button"
          className={`fi-pertence-modo${modo === 'areas' ? ' on' : ''}`}
          onClick={() => setModo('areas')}
        >
          Área
        </button>
        <button
          type="button"
          className={`fi-pertence-modo${modo === 'locais' ? ' on' : ''}`}
          onClick={() => setModo('locais')}
        >
          Local
        </button>
      </div>

      <SeletorDeRelacao
        key={modo}
        alvo={modo}
        unico
        selecionados={escolhido ? [escolhido] : []}
        aoAbrir={aoAbrir}
        aoMudar={(ids) =>
          ids.length === 0
            ? aoAplicar(modo === 'locais' ? 'removerPai' : 'removerArea', true)
            : aoAplicar(modo === 'locais' ? 'paiId' : 'areaId', ids[0])
        }
      />

      {/* A área herdada, dita em voz baixa: quem escolheu um local pai não escolheu
          a área, mas continua precisando saber em qual delas caiu. */}
      {modo === 'locais' && pai && area && (
        <span className="fi-pertence-nota">em {area.nome}</span>
      )}
    </div>
  );
}

/** A nota com que a API marca o que está pendurado no local padrão de uma área. */
const GERAL = 'Área em geral';

/**
 * Campo que vem de consulta, não de coluna. Mostra os chips e, quando a relação é
 * editável pelo outro lado, o seletor que escreve lá — a mesma escrita que a tela
 * daquela ficha faria.
 */
function Derivado({
  campo,
  itens,
  aoAbrir,
  colecao,
  fichaId,
  localPadraoId,
  aoRecarregar,
}: {
  campo: Campo;
  itens: Resumo[];
  aoAbrir: (colecao: Colecao, id: number) => void;
  colecao: Colecao;
  fichaId: number;
  /** Só a Área tem: é onde ficam os NPCs e itens "da área em geral". */
  localPadraoId?: number;
  aoRecarregar: () => void;
}) {
  const [ocupado, setOcupado] = useState(false);

  async function vincular(ids: number[]) {
    const atuais = new Set(itens.map((i) => i.id));
    const novos = ids.filter((i) => !atuais.has(i));
    const removidos = itens.filter((i) => !ids.includes(i.id));

    // NPC "na área" mora, na verdade, no local padrão dela. Sem esse id não há onde
    // colocá-lo — a Área não tem coluna de NPCs, e é isso que impede a duplicata.
    const pelaPadrao =
      campo.vincular === 'npcNaArea' ||
      campo.vincular === 'itemNaArea' ||
      campo.vincular === 'questNaArea';
    if (pelaPadrao && localPadraoId == null) return;

    setOcupado(true);
    try {
      if (campo.vincular === 'itemNaArea' || campo.vincular === 'questNaArea') {
        // Item não tem endpoint de vínculo próprio: ele mora numa lista do Local, e
        // mexer nela pelo local padrão é o equivalente ao que a tela do Local faria.
        //
        // Só os itens do padrão são reescritos. Os que vêm de um local nomeado
        // aparecem aqui com o nome dele na nota, e continuam pertencendo àquele
        // local — tirá-los daqui seria editar um lugar que não está na tela.
        const doPadrao = itens.filter((i) => i.nota === GERAL).map((i) => i.id);
        const atualizada = [...doPadrao.filter((id) => ids.includes(id)), ...novos];
        const lista = campo.vincular === 'itemNaArea' ? 'itens' : 'quests';
        await mundoApi.salvar('locais', localPadraoId!, { [lista]: atualizada });
      } else {
        for (const alvo of novos) {
          if (campo.vincular === 'localNaArea') await mundoApi.vincularLocalNaArea(fichaId, alvo);
          else if (campo.vincular === 'npcNaArea')
            await mundoApi.vincularNpcNoLocal(localPadraoId!, alvo);
          else await mundoApi.vincularNpcNoLocal(fichaId, alvo);
        }
      }
      if (campo.vincular !== 'itemNaArea' && campo.vincular !== 'questNaArea') {
        for (const fora of removidos) {
          if (campo.vincular === 'localNaArea')
            await mundoApi.desvincularLocalDaArea(fichaId, fora.id);
          else if (campo.vincular === 'npcNaArea')
            await mundoApi.desvincularNpcDoLocal(localPadraoId!, fora.id);
          else await mundoApi.desvincularNpcDoLocal(fichaId, fora.id);
        }
      }
      aoRecarregar();
    } finally {
      setOcupado(false);
    }
  }

  if (campo.vincular && (colecao === 'areas' || colecao === 'locais')) {
    return (
      <SeletorDeRelacao
        alvo={campo.alvo!}
        selecionados={itens}
        aoAbrir={aoAbrir}
        aoMudar={(ids) => void vincular(ids)}
        desabilitado={ocupado}
      />
    );
  }

  return (
    <div className="rel">
      <div className="rel-chips">
        {itens.map((item) => (
          <span key={`${item.colecao}-${item.id}`} className="rel-chip leitura">
            {item.imagem && <CapaAutenticada className="rel-mini" url={item.imagem} mini />}
            <button type="button" className="rel-nome" onClick={() => aoAbrir(item.colecao, item.id)}>
              {item.nome}
            </button>
            {item.nota && <span className="rel-nota">{item.nota}</span>}
          </span>
        ))}
      </div>
    </div>
  );
}

/**
 * A receita de um item: outros itens, cada um com a sua quantidade.
 *
 * <p>A quantidade é dado da <b>relação</b>, não do ingrediente — "3 de minério" não é
 * propriedade do minério nem da espada. Por isso ela mora ao lado de cada linha, e não
 * dentro da ficha do ingrediente.
 */
function Receita({
  itemId,
  ingredientes,
  aoAbrir,
  aoRecarregar,
}: {
  itemId: number;
  ingredientes: Ingrediente[];
  aoAbrir: (colecao: Colecao, id: number) => void;
  aoRecarregar: () => void;
}) {
  const escolhidos = ingredientes.map((i) => i.item);

  async function mudar(ids: number[]) {
    const atuais = new Set(escolhidos.map((i) => i.id));
    for (const novo of ids.filter((i) => !atuais.has(i))) {
      await mundoApi.salvarIngrediente(itemId, novo, 1);
    }
    for (const fora of escolhidos.filter((i) => !ids.includes(i.id))) {
      await mundoApi.excluirIngrediente(itemId, fora.id);
    }
    aoRecarregar();
  }

  return (
    <div className="fi-relracas">
      <SeletorDeRelacao
        alvo="itens"
        selecionados={escolhidos}
        aoAbrir={aoAbrir}
        aoMudar={(ids) => void mudar(ids)}
      />
      {ingredientes.map((ing) => (
        <div key={ing.item.id} className="fi-relraca">
          <span className="fi-relraca-nome">{ing.item.nome}</span>
          <input
            className="fi-txt fi-qtd"
            type="number"
            min={1}
            defaultValue={ing.quantidade}
            onBlur={async (e) => {
              const quantidade = Number(e.target.value);
              if (quantidade >= 1 && quantidade !== ing.quantidade) {
                await mundoApi.salvarIngrediente(itemId, ing.item.id, quantidade);
                aoRecarregar();
              }
            }}
          />
        </div>
      ))}
    </div>
  );
}

/** As relações entre raças: escolher a outra raça e escrever o que há entre as duas. */
function RelacoesDeRaca({
  racaId,
  registro,
  aoAbrir,
  aoRecarregar,
}: {
  racaId: number;
  registro: Record<string, unknown>;
  aoAbrir: (colecao: Colecao, id: number) => void;
  aoRecarregar: () => void;
}) {
  const relacoes = (registro.relacoes as { raca: Resumo; texto: string }[]) ?? [];
  const escolhidas = relacoes.map((r) => r.raca);

  async function mudar(ids: number[]) {
    const atuais = new Set(escolhidas.map((r) => r.id));
    for (const novo of ids.filter((i) => !atuais.has(i))) {
      await mundoApi.salvarRelacaoDeRaca(racaId, novo, '');
    }
    for (const fora of escolhidas.filter((r) => !ids.includes(r.id))) {
      await mundoApi.excluirRelacaoDeRaca(racaId, fora.id);
    }
    aoRecarregar();
  }

  return (
    <div className="fi-relracas">
      <SeletorDeRelacao
        alvo="racas"
        selecionados={escolhidas}
        aoAbrir={aoAbrir}
        aoMudar={(ids) => void mudar(ids)}
      />
      {relacoes.map((r) => (
        <div key={r.raca.id} className="fi-relraca">
          <span className="fi-relraca-nome">{r.raca.nome}</span>
          <input
            className="fi-txt"
            defaultValue={r.texto}
            placeholder="Conflito, vassalagem, aliança…"
            onBlur={async (e) => {
              if (e.target.value !== r.texto) {
                await mundoApi.salvarRelacaoDeRaca(racaId, r.raca.id, e.target.value);
                aoRecarregar();
              }
            }}
          />
        </div>
      ))}
    </div>
  );
}

/** Envio de imagem por clique ou arrastar-e-soltar. */
function CampoDeImagem({
  valor,
  aoMudar,
  aoAvisar,
}: {
  valor?: string;
  aoMudar: (url: string | null) => void;
  aoAvisar: (a: { texto: string; ok: boolean }) => void;
}) {
  const campo = useRef<HTMLInputElement>(null);
  const [enviando, setEnviando] = useState(false);
  const [sobre, setSobre] = useState(false);
  const imagem = useImagemAutenticada(valor);

  async function enviar(arquivo?: File) {
    if (!arquivo) return;
    setEnviando(true);
    try {
      const salvo = await mundoApi.enviarImagem(arquivo);
      aoMudar(salvo.url);
    } catch (e) {
      aoAvisar({ texto: e instanceof Error ? e.message : 'Falha no envio.', ok: false });
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div
      className={`fi-imagem${sobre ? ' sobre' : ''}`}
      style={imagem ? { backgroundImage: `url('${imagem}')` } : undefined}
      onClick={() => campo.current?.click()}
      onDragOver={(e) => {
        e.preventDefault();
        setSobre(true);
      }}
      onDragLeave={() => setSobre(false)}
      onDrop={(e) => {
        e.preventDefault();
        setSobre(false);
        void enviar(e.dataTransfer.files[0]);
      }}
      title={valor ? 'Trocar imagem' : 'Enviar imagem'}
    >
      {enviando && <span className="fi-imagem-txt">Enviando…</span>}
      {!enviando && !valor && <span className="fi-imagem-txt">Arraste ou clique</span>}
      {!enviando && valor && (
        <button
          className="fi-imagem-x"
          onClick={(e) => {
            e.stopPropagation();
            aoMudar(null);
          }}
          title="Remover imagem"
        >
          ×
        </button>
      )}
      <input
        ref={campo}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        style={{ display: 'none' }}
        onChange={(e) => {
          void enviar(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
    </div>
  );
}
